import {
  CSSProperties,
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  CardAuthor,
  CardReactions,
  TogetherTypes,
  abbreviateNumber,
} from '@hhgtech/hhg-components'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Comment, Heart } from '@hhgtech/icons/other'
import { Box, BoxProps, Flex } from '@mantine/core'
import dayjs from 'dayjs'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import {
  getHelloSitesUrl,
  getUrlToRedirectToSSOWithReturnUrl,
} from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { CardReactionType } from 'components/pages/expert/_cardPost'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { IS_SSOV2_ENABLED } from 'interfaces/constants'
import { AppContext } from 'state/appContext'

import useStyles from './index.styles'

export type RelevantCardPostType = {
  joinedCommunity?: boolean
} & TogetherTypes.Post

export type Props = {
  post: TogetherTypes.Post
  mobileBanner: string
  desktopBanner: string
  className?: string
  shareUrl?: string
  title?: string
  style?: CSSProperties
  jointCommunityBtn?: boolean
  joinCommunity?: (id: number | string) => Promise<any>
  applyH1?: boolean
} & BoxProps

export const Item = ({
  post,
  mobileBanner,
  desktopBanner,
  styles,
  className,
  classNames,
  applyH1,
  ...rest
}: Props) => {
  const { state } = useContext(AppContext)
  const { isLoggedIn } = state || {}
  const { likesCount: likeCountProp = 0, liked, commentsCount = 0 } = post
  const isMobile = useIsMobile()

  const { locale } = useRouterExtend()
  const [isLike, setLike] = useState(false)
  const [firstMount, setFirstMount] = useState(true)
  const [likeCount, setLikeCount] = useState(likeCountProp)

  const now = dayjs()
  const postEnd = dayjs(post?.eventEnd)
  // const postEnd = dayjs('2023-06-21')
  const isEventExpired = Boolean(
    post?.isMinigame && postEnd.isBefore(now, 'dates'),
  )
  const postRelateiveHref = `/community/${post?.community?.slug || '_'}/${
    post?.slug || ''
  }/`
  const { classes, cx, theme } = useStyles(
    { isEventExpired },
    {
      name: 'CarePathCommunityWithPostsItem',
      styles,
      classNames,
    },
  )
  const onClickPost = (e?: MouseEvent<HTMLAnchorElement>) => {
    e?.preventDefault()
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=/${post?.community?.slug}/${
            post?.slug
          }`,
        })
      } else {
        window.location.href = getUrlToRedirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
            post?.community?.slug
          }/${post?.slug}`,
        )
      }
    } else {
      window.location.href = postRelateiveHref
    }
  }
  const author = post?.author
  const name = author?.name || author?.username || ''
  const username = author?.username || author?.name || ''
  const avatar = author?.avatar
  const authorProps: any = {
    isAnonymous: post?.isAnonymous,
    avatar: avatar,
    avatarProps: {
      size: isMobile ? 40 : 80,
    },
    role: author?.role,
    name: name,
    username: username,
    email: author?.email,
    timeText: post?.createdTime,
    community: post?.community,
    showDetailPopup: true,
    userId: author?.id,
    profileModalProps: {
      zIndex: 1000,
    },
  }
  const handleLikeClick = async () => {
    if (!isLoggedIn) {
      post?.community?.slug &&
        redirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
            post.community.slug
          }`,
        )
    } else {
      await callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_LIKE_POST,
        {
          params: {
            postId: post.id,
          },
        },
        'together',
      )
      setLike((pre) => !pre)
    }
  }

  useEffect(() => {
    setLike(Boolean(post?.liked))
  }, [post])

  useEffect(() => {
    !firstMount &&
      (isLike
        ? setLikeCount(liked ? likeCountProp : likeCountProp + 1)
        : setLikeCount(likeCountProp ? likeCountProp - 1 : likeCountProp))
    setFirstMount(false)
  }, [isLike])

  const photoPreview = useMemo(
    () => (
      <img
        className={classes.imgThumb}
        src={post?.photos?.[0] || '/images/default-image.jpg'}
        alt={post?.title}
        style={
          !post?.photos?.[0]
            ? { maxWidth: '400px', margin: '0 auto', display: 'block' }
            : undefined
        }
        loading="lazy"
      />
    ),
    [post],
  )

  const tracking = {
    'data-event-category': 'Together Embedded',
    'data-event-action': 'Click Post',
    'data-event-label': `https://${domainLocales[locale]}${postRelateiveHref}`,
  }

  const hrefProps = {
    ...tracking,
    href: postRelateiveHref,
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      onClickPost(e)
    },
  }

  return (
    <Box className={cx(classes.root, className)} {...rest}>
      <CardAuthor className={classes.author} {...authorProps}>
        <Box className={classes.content} component="a" {...hrefProps}>
          <CardAuthor.MainText>
            <Text size={isMobile ? 'h4' : 'h3'} as={applyH1 ? 'h1' : 'h3'}>
              {post?.title}
            </Text>
          </CardAuthor.MainText>
          <CardAuthor.SubText>
            <Text
              as="div"
              size={isMobile ? 'p4' : 'p3'}
              sx={{
                '> *': {
                  display: 'inline-block',
                  verticalAlign: 'middle',
                },
              }}
            >
              <CardAuthor.DisplayName />
              <CardAuthor.AuthorBadge sx={{ color: theme.colors.gray[8] }} />
              <CardAuthor.SubDivider />
              {post?.createdTime}
            </Text>
          </CardAuthor.SubText>
        </Box>
      </CardAuthor>

      <Box className={classes.body}>
        <Box
          component="a"
          className={classes.bodyHref}
          sx={{ display: 'block' }}
          {...hrefProps}
        >
          {isMobile ? (
            mobileBanner ? (
              <img
                className={classes.imgThumb}
                src={mobileBanner}
                alt={post?.title}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/images/default-image.jpg'
                  e.currentTarget.style.maxHeight = '400px'
                  e.currentTarget.style.margin = '0 auto'
                  e.currentTarget.style.display = 'block'
                }}
              />
            ) : (
              photoPreview
            )
          ) : desktopBanner ? (
            <img
              className={classes.imgThumb}
              src={desktopBanner}
              alt={post?.title}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = '/images/default-image.jpg'
                e.currentTarget.style.maxHeight = '400px'
                e.currentTarget.style.margin = '0 auto'
                e.currentTarget.style.display = 'block'
              }}
            />
          ) : (
            photoPreview
          )}
        </Box>
        <CardReactions
          trackingCategory={'Together Embedded'}
          trackingLabel={`https://${domainLocales[locale]}${postRelateiveHref}`}
          type={CardReactionType.post}
          loveCount={likeCount}
          replyCount={post?.commentsCount}
          className={`post-reaction`}
          liked={isLike}
          hasShare={false}
          onCommentClick={() => {
            onClickPost()
          }}
          onLikeClick={handleLikeClick}
        >
          <Flex
            align={'center'}
            justify={'flex-end'}
            className={classes.reaction}
          >
            <CardReactions.LikeWrapper {...tracking}>
              <Box className={classes.reactItem}>
                <Heart
                  isSolid={isLike}
                  color={isLike ? theme.colors.red[8] : undefined}
                />
                <Text size="p4">{abbreviateNumber(likeCount)}</Text>
              </Box>
            </CardReactions.LikeWrapper>
            <CardReactions.CommentWrapper {...tracking}>
              <Box className={classes.reactItem}>
                <Comment />
                <Text size="p4">{abbreviateNumber(commentsCount)}</Text>
              </Box>
            </CardReactions.CommentWrapper>
          </Flex>
        </CardReactions>
      </Box>
    </Box>
  )
}
